import { makeStyles } from "tss-react/mui"
import { useEffect, useMemo, useState } from "react"
import { StandardDialog } from "../../../shared/components/dialogs/StandardDialog"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { getOrderDetails, getPublicOrderDetails, OrderDetailsResponse, OrdersQuery } from "../ordersApi"
import { ErrorView } from "../../../shared/components/errors/ErrorView"
import GeneralErrorAnim from "../../../shared/assets/general-error-lottie-anim.json"
import { Box, Stack } from "@mui/system"
import { useParams } from "react-router-dom"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"
import { OrdersPOSType } from "../../support/reports/orders/pos/pages/posTypes"
import { CompaniesOrderItem } from "../../company/reports/accounting/pos/posTypes"
import { Order as PurchaseOrder } from "../../office/reports/purchases/purchaseTypes"
import { Order as KitchenOrder } from "../../merchant/controlKitchenTypes"
import { MeetingServiceOrder as AccountingMeetingServiceOrder } from "../../merchant/accounting/accounting/accountingTypes"
import { MeetingServiceOrder as MeetingServiceOrder } from "../../office/meetingService/meetingServiceTypes"
import {
    fetchPosOrdersForExport,
    MerchantMeetingServiceOrdersQuery,
    PosOrdersForExportResponse,
} from "../../merchant/merchant/production/meetingService/meetingServiceApi"
import { useMerchant } from "../../merchants/merchantHooks"
import { ReceiptDetails } from "../components/ReceiptDetails"
import { Grid } from "@mui/material"
import { ReceiptHeder } from "../../../shared/components/receipt/ReceiptHeder"
import { tailwindPalette } from "../../theme/tailwindPalette"

export type Receipt = Readonly<{
    orderId: number
    orderUid: string
    orderType: string
}>

export type AnyOrder =
    | OrdersPOSType
    | CompaniesOrderItem
    | PurchaseOrder
    | KitchenOrder
    | MeetingServiceOrder
    | AccountingMeetingServiceOrder
type WithId<T> = T extends { id: infer I } ? { id: I } : never
type OrderIdOnly = WithId<AnyOrder>
export type ReceiptOder = ReadonlyArray<OrderIdOnly>

type ReceiptProps = Readonly<{
    isPublic?: boolean
    receipt?: Receipt | null
    receiptsForMultipleView?: AnyOrder[]
    open?: boolean
    onClose?: () => void
    orders?: ReceiptOder
}>

const transformOrdersForExport = (data: PosOrdersForExportResponse) => data?.orders
const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)

const getIndexOfPreviewedOrder = (orders?: ReceiptOder, orderId?: number) => {
    if (!orderId || !orders?.length) return 0
    return orders.findIndex((o) => o.id === orderId)
}

const useStyles = makeStyles()((theme) => ({
    container: {
        backgroundColor: tailwindPalette.slate[100],
        minHeight: "100vh",
        justifyContent: "start",
        paddingBottom: "3.5rem",
        "@media print": {
            display: "block",
            backgroundColor: "transparent",
            padding: 0,
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
    },
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
    },
    slide: {
        width: "100%",
    },
}))

export function ReceiptDialog({
    receipt,
    receiptsForMultipleView,
    isPublic,
    open = false,
    onClose = () => {},
    orders = [],
}: ReceiptProps) {
    const { classes } = useStyles()
    const { orderUid: uid } = useParams()
    const orderId = receipt?.orderId || 0
    const isMultiple = Boolean(receiptsForMultipleView?.length)
    const [currentMeetingIndex, setCurrentMeetingIndex] = useState<number>(
        getIndexOfPreviewedOrder(orders, receipt?.orderId),
    )
    const merchant = isMultiple ? useMerchant() : { merchantId: 0 }

    const activeOrder = useMemo(() => orders?.[currentMeetingIndex], [orders, currentMeetingIndex])
    const orderIds = useMemo(
        () => receiptsForMultipleView?.map((receipt) => Number(receipt.id)),
        [receiptsForMultipleView],
    )

    const orderAuthResponse = useApiQuery(getOrderDetails, {
        queryName: OrdersQuery.ORDER_DETAILS,
        dependencies: { orderId: activeOrder?.id ?? 0 },
        enabled: Boolean(orderId) && !isPublic && !!activeOrder && !isMultiple,
        select: transformData,
    })

    const orderPublicResponse = useApiQuery(getPublicOrderDetails, {
        queryName: OrdersQuery.ORDER_DETAILS_PUBLIC,
        dependencies: { orderUid: uid ?? "" },
        enabled: Boolean(uid) && isPublic && !isMultiple,
        select: transformData,
    })

    const {
        data: exportOrders = [],
        isFetching: isFetchingOrdersForExport,
        error: exportError,
    } = useApiQuery(fetchPosOrdersForExport, {
        queryName: MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT,
        dependencies: {
            merchantId: merchant?.merchantId ?? 0,
            orderIds: orderIds,
        },
        enabled: isMultiple,
        select: transformOrdersForExport,
    })

    const isLoading = isPublic
        ? orderPublicResponse.isFetching
        : orderAuthResponse.isFetching || isFetchingOrdersForExport
    const orderDetails = orderAuthResponse.data || orderPublicResponse.data
    const isError = orderAuthResponse.error || orderPublicResponse.error || exportError

    const goToPreviousMeeting = () => {
        if (currentMeetingIndex > 0) {
            const prevIndex = currentMeetingIndex - 1
            setCurrentMeetingIndex(prevIndex)
        }
    }
    const goToNextMeeting = () => {
        if (currentMeetingIndex < orders?.length - 1) {
            const nextIndex = currentMeetingIndex + 1
            setCurrentMeetingIndex(nextIndex)
        }
    }

    const handleOrderChange = (event: any) => {
        const selectedId = event.target.value
        const index = getIndexOfPreviewedOrder(orders, selectedId)
        setCurrentMeetingIndex(index)
    }

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden" // disable scrolling
        } else {
            document.body.style.overflow = "" // restore
        }

        return () => {
            document.body.style.overflow = "" // cleanup on unmount
        }
    }, [open])

    const content = isError ? (
        <ErrorView
            lottieAnimation={GeneralErrorAnim}
            title="A problem occurred"
            message="Failed to load"
            buttonLabel="Close"
            onClose={onClose}
        />
    ) : (
        <Stack direction="column" className={classes.container}>
            <ReceiptHeder
                activeOrder={activeOrder}
                handleOrderChange={handleOrderChange}
                uid={uid}
                variant="pos"
                orderIdsLength={orderIds?.length}
                isSelectMode={isMultiple}
                orderDetails={orderDetails}
                orders={orders}
                onClose={onClose}
                isAuthenticated={true}
                goToPreviousOrder={goToPreviousMeeting}
                goToNextOrder={goToNextMeeting}
                currentMeetingIndex={currentMeetingIndex}
                ordersLength={orders?.length}
            />
            {isLoading ? (
                <Grid container direction="column" component="main" alignItems="center" justifyContent="center">
                    <LoadingSpinner />
                </Grid>
            ) : (
                <Box id="printable-content" className={classes.slideContainer}>
                    <Stack direction="column" spacing="2rem" className={classes.slide}>
                        {isMultiple ? (
                            exportOrders.map((order) => <ReceiptDetails orderId={order.id} order={order} />)
                        ) : orderDetails ? (
                            <ReceiptDetails orderId={orderDetails.id} order={orderDetails} />
                        ) : null}
                    </Stack>
                </Box>
            )}
        </Stack>
    )

    if (isPublic) {
        return <Box>{content}</Box>
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="receipt-title">
                    <span>Kvittering</span>
                </LocalizedStrict>
            }
            isFullScreen
            hideCloseIcon
            topbar={<></>}
            open={open}
            scroll="paper"
            onClose={onClose}
        >
            {content}
        </StandardDialog>
    )
}
